/**
 * Bake live decal stamps into the wall's albedo/roughness atlas.
 * Uses the live BakedSurfaceMaterial program (BakedDamageComposite + bake-output)
 * so composition cannot drift from the on-screen path.
 */
import * as THREE from 'three';
import { SURFACE_REST_POSITION_ATTRIBUTE } from './SurfaceCoordinates';
import type { PackedDecalStamps } from '../contracts/decalStamps';
import {
  damageDecalUniforms,
  setDecalWallOrigin,
  writeDecalStampUniforms,
} from './DamageDecalAtlas';
import { damageBakeDownUniforms } from './BakedSurfaceMaterial';
import {
  BAKE_FACE_LAYOUTS,
  type BakeFaceId,
  type BakeWallSpec,
  type BakedAtlasMaps,
  type FaceLayout,
} from './SurfaceBaker';
import {
  BAKE_OUTPUT_ALBEDO,
  BAKE_OUTPUT_OFF,
  BAKE_OUTPUT_ROUGHNESS,
  setBakeOutput,
} from './SurfaceBakeChunks';

export interface WallBakeDownTarget {
  atlas: BakedAtlasMaps;
  material: THREE.MeshPhysicalMaterial;
  wall: BakeWallSpec;
}

export interface BakeDownResult {
  ms: number;
  faces: BakeFaceId[];
}

const BLIT_VS = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const BLIT_FS = /* glsl */ `
uniform sampler2D tSrc;
varying vec2 vUv;
void main() {
  gl_FragColor = texture2D(tSrc, vUv);
}
`;

function makeTarget(
  width: number,
  height: number,
  colorSpace: THREE.ColorSpace,
  sampling?: { mipmaps: boolean; anisotropy: number },
  depth = false,
): THREE.WebGLRenderTarget {
  const mipmaps = sampling?.mipmaps === true;
  const rt = new THREE.WebGLRenderTarget(width, height, {
    type: THREE.UnsignedByteType,
    format: THREE.RGBAFormat,
    minFilter: mipmaps ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    generateMipmaps: mipmaps,
    colorSpace,
    depthBuffer: depth,
    stencilBuffer: false,
  });
  rt.texture.wrapS = THREE.ClampToEdgeWrapping;
  rt.texture.wrapT = THREE.ClampToEdgeWrapping;
  rt.texture.colorSpace = colorSpace;
  if (sampling) rt.texture.anisotropy = sampling.anisotropy;
  return rt;
}

function regionPixels(atlas: BakedAtlasMaps, id: BakeFaceId): { x: number; y: number; w: number; h: number } {
  const r = atlas.regions[id];
  const x = Math.round(r.u0 * atlas.width);
  const y = Math.round(r.v0 * atlas.height);
  const w = Math.round((r.u1 - r.u0) * atlas.width);
  const h = Math.round((r.v1 - r.v0) * atlas.height);
  return { x, y, w, h };
}

function blitToRegion(
  renderer: THREE.WebGLRenderer,
  src: THREE.Texture,
  dst: THREE.WebGLRenderTarget,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  const mat = new THREE.ShaderMaterial({
    uniforms: { tSrc: { value: src } },
    vertexShader: BLIT_VS,
    fragmentShader: BLIT_FS,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  const scene = new THREE.Scene();
  scene.add(mesh);
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  renderer.setRenderTarget(dst);
  renderer.setViewport(x, y, width, height);
  renderer.setScissor(x, y, width, height);
  renderer.setScissorTest(true);
  renderer.autoClear = false;
  renderer.render(scene, camera);
  mesh.geometry.dispose();
  mat.dispose();
}

function blitFull(
  renderer: THREE.WebGLRenderer,
  src: THREE.Texture,
  dst: THREE.WebGLRenderTarget,
): void {
  blitToRegion(renderer, src, dst, 0, 0, dst.width, dst.height);
}

function generateTargetMips(renderer: THREE.WebGLRenderer, rt: THREE.WebGLRenderTarget): void {
  renderer.initRenderTarget(rt);
  const gl = renderer.getContext();
  const props = renderer.properties.get(rt.texture) as { __webglTexture?: WebGLTexture };
  if (!props.__webglTexture) return;
  gl.bindTexture(gl.TEXTURE_2D, props.__webglTexture);
  gl.generateMipmap(gl.TEXTURE_2D);
  gl.bindTexture(gl.TEXTURE_2D, null);
}

interface RendererSnap {
  tm: THREE.ToneMapping;
  exp: number;
  cs: THREE.ColorSpace;
  target: THREE.WebGLRenderTarget | null;
  autoClear: boolean;
  clear: THREE.Color;
  alpha: number;
  viewport: THREE.Vector4;
  scissor: THREE.Vector4;
  scissorTest: boolean;
}

function snapshotRenderer(renderer: THREE.WebGLRenderer): RendererSnap {
  const clear = new THREE.Color();
  renderer.getClearColor(clear);
  return {
    tm: renderer.toneMapping,
    exp: renderer.toneMappingExposure,
    cs: renderer.outputColorSpace as THREE.ColorSpace,
    target: renderer.getRenderTarget() as THREE.WebGLRenderTarget | null,
    autoClear: renderer.autoClear,
    clear,
    alpha: renderer.getClearAlpha(),
    viewport: renderer.getViewport(new THREE.Vector4()),
    scissor: renderer.getScissor(new THREE.Vector4()),
    scissorTest: renderer.getScissorTest(),
  };
}

function restoreRenderer(renderer: THREE.WebGLRenderer, snap: RendererSnap): void {
  renderer.setRenderTarget(snap.target);
  renderer.toneMapping = snap.tm;
  renderer.toneMappingExposure = snap.exp;
  renderer.outputColorSpace = snap.cs;
  renderer.autoClear = snap.autoClear;
  renderer.setClearColor(snap.clear, snap.alpha);
  renderer.setViewport(snap.viewport);
  renderer.setScissor(snap.scissor);
  renderer.setScissorTest(snap.scissorTest);
}

function atlasTarget(
  atlas: BakedAtlasMaps,
  kind: 'albedo' | 'roughness' | 'normal',
): THREE.WebGLRenderTarget | null {
  const tex = atlas[kind];
  return atlas.targets.find((rt) => rt.texture === tex) ?? null;
}

function makeFacePlane(
  layout: FaceLayout,
  wall: BakeWallSpec,
  atlas: BakedAtlasMaps,
  material: THREE.Material,
): { mesh: THREE.Mesh; camera: THREE.OrthographicCamera } {
  const halfU = layout.halfU(wall.width, wall.height, wall.thickness);
  const halfV = layout.halfV(wall.width, wall.height, wall.thickness);
  const halfN = layout.halfN(wall.width, wall.height, wall.thickness);
  const geom = new THREE.PlaneGeometry(2 * halfU, 2 * halfV, 1, 1);
  const pos = geom.getAttribute('position');
  const uv = geom.getAttribute('uv');
  const region = atlas.regions[layout.id];
  const rest = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i += 1) {
    const u = uv.getX(i);
    const v = uv.getY(i);
    uv.setXY(
      i,
      region.u0 + u * (region.u1 - region.u0),
      region.v0 + v * (region.v1 - region.v0),
    );
    const ru = (u - 0.5) * 2 * halfU;
    const rv = (v - 0.5) * 2 * halfV;
    rest[i * 3] = layout.tangent.x * ru + layout.bitangent.x * rv + layout.normal.x * halfN;
    rest[i * 3 + 1] = layout.tangent.y * ru + layout.bitangent.y * rv + layout.normal.y * halfN;
    rest[i * 3 + 2] = layout.tangent.z * ru + layout.bitangent.z * rv + layout.normal.z * halfN;
  }
  geom.setAttribute(SURFACE_REST_POSITION_ATTRIBUTE, new THREE.BufferAttribute(rest, 3));
  try {
    geom.computeTangents();
  } catch {
    /* keep going */
  }
  const mesh = new THREE.Mesh(geom, material);
  mesh.position.copy(layout.normal).multiplyScalar(halfN);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), layout.normal);
  mesh.updateMatrixWorld(true);

  const camera = new THREE.OrthographicCamera(-halfU, halfU, halfV, -halfV, 0.002, 0.35);
  camera.up.copy(layout.bitangent);
  camera.position.copy(mesh.position).addScaledVector(layout.normal, 0.04);
  camera.lookAt(mesh.position);
  camera.updateProjectionMatrix();
  camera.updateMatrixWorld(true);
  return { mesh, camera };
}

/**
 * Re-render each atlas face with the live composite applied for `stamps` only,
 * then replace the wall's albedo + roughness (mips regenerated).
 */
export function bakeDownStamps(
  renderer: THREE.WebGLRenderer,
  wallBake: WallBakeDownTarget,
  stamps: PackedDecalStamps,
): BakeDownResult {
  const t0 = performance.now();
  const { atlas, material, wall } = wallBake;
  const albedoRT = atlasTarget(atlas, 'albedo');
  const roughnessRT = atlasTarget(atlas, 'roughness');
  if (!albedoRT || !roughnessRT) {
    return { ms: 0, faces: [] };
  }
  if (stamps.count <= 0) {
    return { ms: performance.now() - t0, faces: [] };
  }

  const prevToneMapped = material.toneMapped;
  const prevOrigin = damageDecalUniforms.uDecalWallOrigin.value.clone();
  setDecalWallOrigin({ x: 0, y: 0, z: 0 });
  const bakeScene = new THREE.Scene();
  bakeScene.background = new THREE.Color(0, 0, 0);
  bakeScene.environment = null;
  bakeScene.fog = null;
  bakeScene.add(new THREE.AmbientLight(0xffffff, 1));

  const snap = snapshotRenderer(renderer);
  const faceTemps: THREE.WebGLRenderTarget[] = [];
  const faceMeshes: THREE.Mesh[] = [];
  damageBakeDownUniforms.uBakeDownDecals.value = 1;
  damageDecalUniforms.uBakeDownDecals.value = 1;
  writeDecalStampUniforms(stamps);
  material.toneMapped = false;

  renderer.toneMapping = THREE.NoToneMapping;
  renderer.toneMappingExposure = 1;
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  renderer.autoClear = true;

  try {
    for (const layout of BAKE_FACE_LAYOUTS) {
      const { mesh, camera } = makeFacePlane(layout, wall, atlas, material);
      faceMeshes.push(mesh);
      bakeScene.clear();
      bakeScene.add(new THREE.AmbientLight(0xffffff, 1));
      bakeScene.add(mesh);

      const rect = regionPixels(atlas, layout.id);
      const albedoFace = makeTarget(rect.w, rect.h, THREE.LinearSRGBColorSpace, undefined, true);
      const roughnessFace = makeTarget(rect.w, rect.h, THREE.NoColorSpace, undefined, true);
      faceTemps.push(albedoFace, roughnessFace);

      renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
      setBakeOutput(material, BAKE_OUTPUT_ALBEDO);
      renderer.setRenderTarget(albedoFace);
      renderer.setViewport(0, 0, rect.w, rect.h);
      renderer.setScissorTest(false);
      renderer.setClearColor(0x000000, 1);
      renderer.autoClear = true;
      renderer.clear();
      renderer.render(bakeScene, camera);
      renderer.initRenderTarget(albedoRT);
      renderer.copyTextureToTexture(
        albedoFace.texture,
        albedoRT.texture,
        new THREE.Box2(new THREE.Vector2(0, 0), new THREE.Vector2(rect.w, rect.h)),
        new THREE.Vector2(rect.x, rect.y),
      );

      renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
      setBakeOutput(material, BAKE_OUTPUT_ROUGHNESS);
      renderer.setRenderTarget(roughnessFace);
      renderer.setViewport(0, 0, rect.w, rect.h);
      renderer.setScissorTest(false);
      renderer.autoClear = true;
      renderer.clear();
      renderer.render(bakeScene, camera);
      renderer.initRenderTarget(roughnessRT);
      renderer.copyTextureToTexture(
        roughnessFace.texture,
        roughnessRT.texture,
        new THREE.Box2(new THREE.Vector2(0, 0), new THREE.Vector2(rect.w, rect.h)),
        new THREE.Vector2(rect.x, rect.y),
      );
    }

    renderer.setScissorTest(false);
    renderer.setViewport(0, 0, atlas.width, atlas.height);
    renderer.autoClear = false;
    generateTargetMips(renderer, albedoRT);
    generateTargetMips(renderer, roughnessRT);
  } finally {
    setBakeOutput(material, BAKE_OUTPUT_OFF);
    damageBakeDownUniforms.uBakeDownDecals.value = 0;
    damageDecalUniforms.uBakeDownDecals.value = 0;
    damageDecalUniforms.uDecalWallOrigin.value.copy(prevOrigin);
    material.toneMapped = prevToneMapped;
    restoreRenderer(renderer, snap);
    for (const mesh of faceMeshes) mesh.geometry.dispose();
    for (const rt of faceTemps) rt.dispose();
  }

  return {
    ms: performance.now() - t0,
    faces: BAKE_FACE_LAYOUTS.map((layout) => layout.id),
  };
}

/** GPU clone of an atlas (contents copied). Used as the reset-to-pristine source. */
export function cloneBakedAtlasMaps(
  renderer: THREE.WebGLRenderer,
  src: BakedAtlasMaps,
): BakedAtlasMaps {
  const aniso = src.albedo.anisotropy || 8;
  const sampling = { mipmaps: true, anisotropy: aniso };
  const albedoRT = makeTarget(src.width, src.height, THREE.LinearSRGBColorSpace, sampling);
  const roughnessRT = makeTarget(src.width, src.height, THREE.NoColorSpace, sampling);
  const normalRT = makeTarget(src.width, src.height, THREE.NoColorSpace, sampling);
  const snap = snapshotRenderer(renderer);
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.toneMappingExposure = 1;
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  try {
    renderer.setScissorTest(false);
    renderer.autoClear = true;
    renderer.setClearColor(0x000000, 1);
    renderer.setRenderTarget(albedoRT);
    renderer.setViewport(0, 0, src.width, src.height);
    renderer.clear();
    blitFull(renderer, src.albedo, albedoRT);
    renderer.setRenderTarget(roughnessRT);
    renderer.clear();
    blitFull(renderer, src.roughness, roughnessRT);
    renderer.setRenderTarget(normalRT);
    renderer.clear();
    blitFull(renderer, src.normal, normalRT);
    generateTargetMips(renderer, albedoRT);
    generateTargetMips(renderer, roughnessRT);
    generateTargetMips(renderer, normalRT);
  } finally {
    restoreRenderer(renderer, snap);
  }
  const textures = [albedoRT.texture, roughnessRT.texture, normalRT.texture];
  const targets = [albedoRT, roughnessRT, normalRT];
  for (const tex of textures) {
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.generateMipmaps = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.anisotropy = aniso;
    tex.needsUpdate = true;
  }
  albedoRT.texture.colorSpace = THREE.LinearSRGBColorSpace;
  return {
    width: src.width,
    height: src.height,
    albedo: albedoRT.texture,
    roughness: roughnessRT.texture,
    normal: normalRT.texture,
    regions: src.regions,
    textures,
    targets,
    dispose() {
      for (const rt of targets) rt.dispose();
    },
  };
}

/** Copy GPU contents from `src` into existing `dst` targets (same size). */
export function copyBakedAtlasMaps(
  renderer: THREE.WebGLRenderer,
  src: BakedAtlasMaps,
  dst: BakedAtlasMaps,
): void {
  const dstAlbedo = atlasTarget(dst, 'albedo');
  const dstRough = atlasTarget(dst, 'roughness');
  const dstNormal = atlasTarget(dst, 'normal');
  if (!dstAlbedo || !dstRough || !dstNormal) return;
  const snap = snapshotRenderer(renderer);
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.toneMappingExposure = 1;
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  try {
    renderer.setScissorTest(false);
    renderer.autoClear = true;
    blitFull(renderer, src.albedo, dstAlbedo);
    blitFull(renderer, src.roughness, dstRough);
    blitFull(renderer, src.normal, dstNormal);
    generateTargetMips(renderer, dstAlbedo);
    generateTargetMips(renderer, dstRough);
    generateTargetMips(renderer, dstNormal);
    dst.albedo.needsUpdate = true;
    dst.roughness.needsUpdate = true;
    dst.normal.needsUpdate = true;
  } finally {
    restoreRenderer(renderer, snap);
  }
}
